import { Box, Button, Card, Collapsible, Text } from '@chakra-ui/react'
import { ChevronDown } from 'lucide-react'

import { Field } from '@mangovoice/mango-ui'

import type { PbxSettingsApi } from '../api/pbxSettingsApi.types'
import type { PbxSettingsTranslateFn } from '../types'
import { text } from '../types'
import type {
  PbxFormValues,
  SelectOption,
  SystemCheckboxConfig,
  SystemSettingsFormValues,
} from '../types/pbxSettings.types'
import {
  CALL_PARK_TIMEOUT_MAX,
  CALL_PARK_TIMEOUT_MIN,
  EXTENSION_DIGITS_OPTIONS,
  SYSTEM_CHECKBOX_FIELDS,
} from '../utils/pbxSettings.constants'
import { BooleanSelectField, InputField, SelectField } from './PbxFormFields'
import { PbxPhoneNumberSelect } from './PbxPhoneNumberSelect'

const getCheckboxValue = (
  config: SystemCheckboxConfig,
  pbx: PbxFormValues,
  systemSettings: SystemSettingsFormValues,
): boolean => {
  if (config.target === 'pbx') return Boolean(pbx[config.key])
  return Boolean(systemSettings[config.key])
}

interface PhoneNumberFieldProps {
  label: string
  value: string
  pbxId: number
  sourceMode: 'caller_id' | 'fax'
  api: PbxSettingsApi
  t?: PbxSettingsTranslateFn
  onChange: (value: string) => void
}

const PhoneNumberField = ({
  label,
  value,
  pbxId,
  sourceMode,
  api,
  t,
  onChange,
}: PhoneNumberFieldProps) => (
  <Field.Root required display="grid" gridTemplateColumns="1fr" gap={2}>
    <Field.Label>{label}</Field.Label>
    <PbxPhoneNumberSelect
      api={api}
      t={t}
      pbxId={pbxId}
      value={value}
      sourceMode={sourceMode}
      onChange={onChange}
      placeholder={text(
        t,
        'pbxSettings.placeholders.selectFromList',
        'Select from the List...',
      )}
    />
  </Field.Root>
)

interface PbxSettingsSystemSettingsSectionProps {
  t?: PbxSettingsTranslateFn
  pbx: PbxFormValues
  systemSettings: SystemSettingsFormValues
  isInternalUser: boolean
  alert911Options: SelectOption[]
  timezoneOptions: SelectOption[]
  virtualFaxOptions: SelectOption[]
  codecSelectOptions: SelectOption[]
  disabledCodecValues?: Set<string>
  pbxId: number
  api: PbxSettingsApi
  virtualFaxLoading?: boolean
  onPbxFieldChange: <K extends keyof PbxFormValues>(
    field: K,
    value: PbxFormValues[K],
  ) => void
  onSystemSettingsFieldChange: <K extends keyof SystemSettingsFormValues>(
    field: K,
    value: SystemSettingsFormValues[K],
  ) => void
}

export const PbxSettingsSystemSettingsSection = ({
  t,
  pbx,
  systemSettings,
  isInternalUser,
  alert911Options,
  timezoneOptions,
  virtualFaxOptions,
  codecSelectOptions,
  disabledCodecValues,
  pbxId,
  api,
  virtualFaxLoading = false,
  onPbxFieldChange,
  onSystemSettingsFieldChange,
}: PbxSettingsSystemSettingsSectionProps) => (
  <Collapsible.Root>
    <Card.Root bg="bg.panel" rounded="xl">
      <Card.Header p={2}>
        <Collapsible.Trigger asChild>
          <Button variant="ghost" justifyContent="space-between" width="full">
            <Box as="span">
              {text(t, 'pbxSettings.sections.system.title', 'System Settings')}{' '}
              <Text as="span" color="fg.error">
                *
              </Text>
            </Box>
            <Collapsible.Indicator
              transition="transform 0.2s"
              _open={{ transform: 'rotate(-180deg)' }}
            >
              <ChevronDown size={16} />
            </Collapsible.Indicator>
          </Button>
        </Collapsible.Trigger>
      </Card.Header>
      <Collapsible.Content>
        <Card.Body p={6} pt={0}>
          <Box display="grid" gridTemplateColumns="1fr" gap={4}>
            <SelectField
              t={t}
              label={text(
                t,
                'pbxSettings.fields.defaultEmergencyAlert.label',
                'Default Emergency Alert',
              )}
              value={systemSettings.alert_911}
              onChange={(value) =>
                onSystemSettingsFieldChange('alert_911', value)
              }
              options={alert911Options}
              placeholder={text(
                t,
                'pbxSettings.placeholders.selectFromList',
                'Select from the List...',
              )}
            />
            <SelectField
              t={t}
              label={text(
                t,
                'pbxSettings.fields.defaultTimeZone.label',
                'Default Time Zone',
              )}
              required
              value={systemSettings.default_time_zone}
              onChange={(value) =>
                onSystemSettingsFieldChange('default_time_zone', value)
              }
              options={timezoneOptions}
              placeholder={text(
                t,
                'pbxSettings.placeholders.selectTimeZone',
                'Select time zone',
              )}
            />
            <InputField
              t={t}
              label={text(
                t,
                'pbxSettings.fields.callParkTimeout.label',
                'Call Park Timeout',
              )}
              required
              type="number"
              min={CALL_PARK_TIMEOUT_MIN}
              max={CALL_PARK_TIMEOUT_MAX}
              value={systemSettings.call_park_timeout}
              onChange={(value) =>
                onSystemSettingsFieldChange('call_park_timeout', value)
              }
            />
            <SelectField
              t={t}
              label={text(
                t,
                'pbxSettings.fields.extensionDigits.label',
                'Extension Digits',
              )}
              required
              value={systemSettings.extension_digits}
              onChange={(value) =>
                onSystemSettingsFieldChange('extension_digits', value)
              }
              options={EXTENSION_DIGITS_OPTIONS}
              placeholder={text(
                t,
                'pbxSettings.placeholders.selectExtensionDigits',
                'Select extension digits',
              )}
            />
            <InputField
              t={t}
              label={text(
                t,
                'pbxSettings.fields.defaultAreaCode.label',
                'Default Area Code',
              )}
              required
              type="number"
              min={200}
              max={998}
              value={systemSettings.default_area_code}
              onChange={(value) =>
                onSystemSettingsFieldChange('default_area_code', value)
              }
            />
            <PhoneNumberField
              label={text(
                t,
                'pbxSettings.fields.defaultCallerId.label',
                'Default Caller ID',
              )}
              pbxId={pbxId}
              api={api}
              t={t}
              sourceMode="caller_id"
              value={systemSettings.default_caller_id}
              onChange={(value) =>
                onSystemSettingsFieldChange('default_caller_id', value)
              }
            />
            <PhoneNumberField
              label={text(
                t,
                'pbxSettings.fields.defaultFaxNumber.label',
                'Default Fax Number',
              )}
              pbxId={pbxId}
              api={api}
              t={t}
              sourceMode="fax"
              value={systemSettings.default_send_fax_number}
              onChange={(value) =>
                onSystemSettingsFieldChange('default_send_fax_number', value)
              }
            />
            <SelectField
              t={t}
              label={text(
                t,
                'pbxSettings.fields.defaultVirtualFax.label',
                'Default Virtual Fax',
              )}
              required
              loading={virtualFaxLoading}
              value={systemSettings.default_fax_extension_id}
              onChange={(value) =>
                onSystemSettingsFieldChange('default_fax_extension_id', value)
              }
              options={virtualFaxOptions}
              placeholder={text(
                t,
                'pbxSettings.placeholders.selectFromList',
                'Select from the List...',
              )}
            />
            <SelectField
              t={t}
              label={text(
                t,
                'pbxSettings.fields.preferredCodec.label',
                'Preferred Codec',
              )}
              required
              value={pbx.preferred_codec}
              onChange={(value) => onPbxFieldChange('preferred_codec', value)}
              options={codecSelectOptions}
              disabledValues={disabledCodecValues}
              placeholder={text(
                t,
                'pbxSettings.placeholders.selectCodec',
                'Select codec',
              )}
            />

            {SYSTEM_CHECKBOX_FIELDS.filter(
              (fieldConfig) => !fieldConfig.internalOnly || isInternalUser,
            ).map((fieldConfig) => (
              <BooleanSelectField
                key={fieldConfig.label}
                required={Boolean(fieldConfig.required)}
                value={getCheckboxValue(fieldConfig, pbx, systemSettings)}
                label={text(
                  t,
                  `pbxSettings.fields.checkboxes.${fieldConfig.key}`,
                  fieldConfig.label,
                )}
                disabled={
                  fieldConfig.readOnly ||
                  (fieldConfig.readOnlyWhenTrue &&
                    getCheckboxValue(fieldConfig, pbx, systemSettings))
                }
                onChange={(value) => {
                  if (fieldConfig.target === 'pbx') {
                    onPbxFieldChange(fieldConfig.key, value)
                    return
                  }
                  onSystemSettingsFieldChange(fieldConfig.key, value)
                }}
              />
            ))}
          </Box>
        </Card.Body>
      </Collapsible.Content>
    </Card.Root>
  </Collapsible.Root>
)
